/**
 * useUpcomingOpponent Hook
 *
 * Fetches upcoming games for the selected team and returns the next opponent.
 * PRIMARY: ESPN scoreboard API (live schedule data with proper dates)
 * FALLBACK: NatStat BigQuery data
 */
import { useState, useEffect, useCallback } from 'react';
import { useSelectedTeam } from '../lib/teamConfig';
import { fetchNatStatGames, fetchTeamRoster } from '../utils/natstatDataService';
import { getBasketballPlayers } from '../utils/basketballDataService';
import { fetchMLBTeamRoster } from '../lib/mlbRosterService';
import axios from 'axios';

// Map NatStat league codes → ESPN schedule endpoint league codes
const LEAGUE_TO_SCHEDULE = {
  NBA: 'NBA',
  WNBA: 'WNBA',
  MBB: 'MBB',
  WBB: 'WBB',
};

/**
 * Parse a date value that might come from BigQuery (object), ESPN (ISO string), or plain string.
 * Returns a valid Date or null.
 */
function parseGameDate(raw) {
  if (!raw) return null;
  // BigQuery DATE objects: { value: "2026-02-15" }
  if (typeof raw === 'object' && raw.value) {
    return new Date(raw.value + 'T00:00:00');
  }
  // ISO string from ESPN (e.g., "2026-02-19T00:30Z")
  const d = new Date(raw);
  if (!isNaN(d.getTime())) return d;
  // Try appending time if it's a bare date string "2026-02-15"
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return new Date(raw + 'T00:00:00');
  }
  return null;
}

/**
 * Fetch schedule from ESPN via our backend endpoint
 */
async function fetchEspnSchedule(leagueCode, teamAbbr) {
  const scheduleLeague = LEAGUE_TO_SCHEDULE[leagueCode];
  if (!scheduleLeague) return null;

  try {
    const url = `/api/basketball/schedule/${scheduleLeague}?days=14${teamAbbr ? `&team=${teamAbbr}` : ''}`;
    const resp = await axios.get(url);
    const data = resp.data?.data || resp.data;
    if (data?.games && Array.isArray(data.games)) {
      return data.games;
    }
    return null;
  } catch (err) {
    console.warn('[useUpcomingOpponent] ESPN schedule fetch failed:', err.message);
    return null;
  }
}

/**
 * Hook to get the next opponent and their roster for the currently selected team
 * @returns {Object} { opponent, opponentRoster, loading, error, nextGame, refetch }
 */
export function useUpcomingOpponent() {
  const { selectedTeam } = useSelectedTeam();
  const [opponent, setOpponent] = useState(null);
  const [opponentRoster, setOpponentRoster] = useState([]);
  const [nextGame, setNextGame] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Extract team details from selection
  const teamCode = selectedTeam?.team?.team_code || selectedTeam?.team?.team_id;
  const teamName = selectedTeam?.team?.team_name || selectedTeam?.team?.name;
  const leagueCode = selectedTeam?.league?.code;

  // Fetch upcoming opponent
  const fetchOpponent = useCallback(async () => {
    if (!teamCode || !leagueCode) {
      setOpponent(null);
      setOpponentRoster([]);
      setNextGame(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let upcomingGame = null;

      // === PRIMARY: ESPN Schedule ===
      const espnGames = await fetchEspnSchedule(leagueCode, teamCode);
      if (espnGames && espnGames.length > 0) {
        const now = new Date();
        // Find first future scheduled game
        const futureGame = espnGames.find(g => {
          const gd = parseGameDate(g.date);
          return gd && gd > now && g.status === 'STATUS_SCHEDULED';
        });
        // Or the most recent game if none are scheduled
        const mostRecent = espnGames[espnGames.length - 1];
        const espnGame = futureGame || mostRecent;

        if (espnGame) {
          // Determine if our team is home or away
          const isHome = espnGame.home_team?.abbreviation === teamCode;
          const oppTeam = isHome ? espnGame.away_team : espnGame.home_team;
          const gameDate = parseGameDate(espnGame.date);

          upcomingGame = {
            game_date: gameDate?.toISOString() || espnGame.date,
            home_team: espnGame.home_team?.name,
            visitor_team: espnGame.away_team?.name,
            home_team_code: espnGame.home_team?.abbreviation,
            visitor_team_code: espnGame.away_team?.abbreviation,
            home_team_logo: espnGame.home_team?.logo,
            visitor_team_logo: espnGame.away_team?.logo,
            status: espnGame.status_detail || espnGame.status,
            home_score: espnGame.home_team?.score,
            visitor_score: espnGame.away_team?.score,
            venue: espnGame.venue,
            broadcast: espnGame.broadcast,
            source: 'espn',
          };
        }
      }

      // === FALLBACK: NatStat BigQuery ===
      if (!upcomingGame) {
        const games = await fetchNatStatGames(leagueCode, { teamId: teamCode, limit: 20 });
        if (games && games.length > 0) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          let found = games.find(game => {
            const gd = parseGameDate(game.game_date);
            const status = (game.status || '').toLowerCase();
            return (gd && gd >= today) || !['final', 'completed', 'f'].includes(status);
          });

          if (!found) {
            found = games.sort((a, b) => {
              const da = parseGameDate(a.game_date);
              const db = parseGameDate(b.game_date);
              return (db?.getTime() || 0) - (da?.getTime() || 0);
            })[0];
          }

          if (found) {
            const gd = parseGameDate(found.game_date);
            upcomingGame = {
              ...found,
              game_date: gd?.toISOString() || found.game_date,
              source: 'natstat',
            };
          }
        }
      }

      if (!upcomingGame) {
        setOpponent(null);
        setOpponentRoster([]);
        setNextGame(null);
        return;
      }

      setNextGame(upcomingGame);

      // Determine opponent based on which team we are
      const isHome = upcomingGame.home_team_code === teamCode;
      const opponentCode = isHome ? upcomingGame.visitor_team_code : upcomingGame.home_team_code;
      const opponentName = isHome ? upcomingGame.visitor_team : upcomingGame.home_team;
      const opponentLogo = isHome ? upcomingGame.visitor_team_logo : upcomingGame.home_team_logo;

      setOpponent({
        team_code: opponentCode,
        team_name: opponentName,
        team_logo: opponentLogo,
        isHome: isHome,
        gameDate: upcomingGame.game_date,
        gameStatus: upcomingGame.status,
        venue: upcomingGame.venue,
        broadcast: upcomingGame.broadcast,
      });

      // Fetch the opponent's roster
      if (opponentCode) {
        let roster = await fetchTeamRoster(leagueCode, opponentCode);

        // Fallback to local data services if NatStat returns empty
        if ((!roster || roster.length === 0) && opponentName) {
          console.log(`NatStat roster empty for ${opponentName}, trying local fallback...`);

          const isBasketball = ['NBA', 'WNBA', 'MBB', 'WBB', 'EURO'].includes(leagueCode);
          const isBaseball = ['MLB'].includes(leagueCode);

          if (isBasketball) {
            const fallbackRoster = await getBasketballPlayers(leagueCode, opponentName);
            if (fallbackRoster && fallbackRoster.length > 0) {
              roster = fallbackRoster;
            }
          } else if (isBaseball) {
            let mlbRoster = await fetchMLBTeamRoster(opponentCode);
            if (!mlbRoster || mlbRoster.length === 0) {
              mlbRoster = await fetchMLBTeamRoster(opponentName);
            }
            if (mlbRoster && mlbRoster.length > 0) {
              roster = mlbRoster.map(p => ({
                player_id: p.player_id || p.mlbId,
                player_name: p.name || `${p.first_name} ${p.last_name}`,
                position: p.position,
                jersey_number: p.number,
                height: p.height,
                weight: p.weight,
                image: p.image_url || p.photo,
                throws: p.throws,
                bats: p.bats,
                team_name: p.team,
              }));
            }
          }
        }

        setOpponentRoster(roster || []);
      }
    } catch (err) {
      console.error('Error fetching upcoming opponent:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch opponent'));
      setOpponent(null);
      setOpponentRoster([]);
      setNextGame(null);
    } finally {
      setLoading(false);
    }
  }, [teamCode, leagueCode]);

  // Auto-fetch when team changes
  useEffect(() => {
    if (selectedTeam) {
      fetchOpponent();
    }
  }, [selectedTeam, fetchOpponent]);

  return {
    opponent,
    opponentRoster,
    nextGame,
    loading,
    error,
    hasOpponent: Boolean(opponent),
    refetch: fetchOpponent,
  };
}

export default useUpcomingOpponent;
